import React, { useContext, useState } from 'react';
import { PapiDeviation } from '@wix/da-papi-types';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { Url } from '@wix/da-url';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { useDeviationAttributes } from '../../../DeviationViews/hooks/useDeviationAttributes';
import { useRestrictToLoggedIn } from '../../../utils/hooks/useRestrictToLoggedIn';
import { useRestrictToLoggedInAndVerified } from '../../../utils/hooks/useRestrictToLoggedInAndVerified';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import {
  getDeviationFreeDownload,
  getDeviationPremiumDownload,
} from './permissions';
import { addErrorNotification } from '../../../Notifications/redux/actionCreators';
import { useDispatch } from 'react-redux';
import DownloadLimitModal from './DownloadLimitModal';

export interface DownloadAction {
  onClick?: () => void;
  href?: string;
  hasAction: boolean;
  limitModal?: React.ReactNode;
}

/**
 * This actually covers free and tier-subscription-gated downloads
 */
export function useFreeDownloadAction(
  deviation: PapiDeviation,
  isOwner: boolean
): DownloadAction {
  const { isLoggedIn } = useContext(SessionContext);
  const { restrictToLoggedInAndVerified } = useRestrictToLoggedInAndVerified();
  const { isDownloadable } = useDeviationAttributes(deviation, isOwner);
  const dispatch = useDispatch();
  const hasDownloadLimits = useFlag('free_download_limits');
  const [showLimitModal, setShowLimitModal] = useState(false);

  const downloadRestrictions = deviation.extended?.downloadRestrictions;
  const isDownloadLimitReached =
    hasDownloadLimits &&
    downloadRestrictions !== undefined &&
    downloadRestrictions.remaining <= 0;

  if (!isDownloadable) {
    return { hasAction: false };
  }

  if (!isLoggedIn) {
    return {
      onClick: restrictToLoggedInAndVerified(
        ReferralType.ACTION_BAR,
        'freedownload'
      ),
      hasAction: true,
    };
  }

  const { download, permission } = getDeviationFreeDownload(deviation);
  if (
    permission === 'needs_subscription' ||
    permission === 'blocked_by_subscription'
  ) {
    return {
      onClick: () =>
        dispatch(
          addErrorNotification(
            'You need to subscribe to this deviant to download this content.'
          )
        ),
      hasAction: true,
    };
  }

  if (isDownloadLimitReached) {
    return {
      onClick: () => setShowLimitModal(true),
      hasAction: true,
      limitModal: showLimitModal
        ? React.createElement(DownloadLimitModal, {
            allocation: downloadRestrictions.allocation,
            onRequestClose: () => setShowLimitModal(false),
          })
        : null,
    };
  }

  return { href: download?.url, hasAction: true };
}

export function usePremiumDownloadAction(
  deviation: PapiDeviation,
  isOwner: boolean
): DownloadAction {
  const { isLoggedIn } = useContext(SessionContext);
  const { isPurchasable, isDownloadable } = useDeviationAttributes(
    deviation,
    isOwner
  );

  const { restrictToLoggedIn } = useRestrictToLoggedIn();

  if (!isDownloadable) {
    return { hasAction: false };
  }

  if (!isLoggedIn) {
    return {
      onClick: restrictToLoggedIn(ReferralType.ACTION_BAR),
      hasAction: true,
    };
  }

  if (!isPurchasable) {
    return { hasAction: false };
  }

  const checkoutUrl = deviation.extended?.pcp?.subproductId
    ? Url.checkoutLink(deviation.extended?.pcp.subproductId)
    : undefined;
  const { download, permission } = getDeviationPremiumDownload(
    deviation,
    isOwner
  );

  return {
    href: permission === 'downloadable' ? download?.url : checkoutUrl,
    hasAction: true,
  };
}
